import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ButtonStyled } from '../../../components/button/button.styled';
import { H3, P } from '../../../styles/text';
import { lightTheme } from '../../../styles/theme';
import type { AnnouncementType } from '../../../types/api/announcements.types';
import { ChatLightbox } from '../../Chat/components/ChatLightbox';
import { ANNOUNCEMENT_TYPE_LABEL } from '../helpers/announcementContent';
import {
  DontShowAgain,
  HeroImage,
  MarkdownBody,
} from './AnnouncementView.styled';

/**
 * "on this app", because that is all it does: the backend keeps the dismissal
 * per app and platform, so silencing it on the dashboard leaves it due on the
 * phone. Promising more than that would be a lie the next login exposes.
 */
export const DONT_SHOW_AGAIN_LABEL = "Don't show this again on this app";

type Props = {
  type: AnnouncementType;
  title: string;
  /** Markdown. react-markdown leaves raw HTML unrendered, so no sanitizing. */
  body: string;
  imageUrl: string | null;
  /** Set instead of `imageUrl` when the announcement is a GIF. */
  gifUrl?: string | null;
  /** Shown when the announcement repeats and can be silenced for good. */
  showDontShowAgain: boolean;
  /** Absent handlers make the view decorative - the wizard's preview. */
  onConfirm?: () => void;
  onDontShowAgain?: () => void;
};

/**
 * An announcement exactly as a client sees it, modeled on the chat feature's
 * What's New modal: red type heading, hero image, bold title, markdown body,
 * one big red button.
 *
 * The one component for both the real modal and the wizard's preview, so the
 * preview cannot drift from what actually ships.
 *
 * Direct imports only, no `@vgrubs/components` - this renders inside the
 * layout, and library code that eagerly re-enters its own barrel is how the
 * silent blank page happens.
 */
export const AnnouncementView = ({
  type,
  title,
  body,
  imageUrl,
  gifUrl,
  showDontShowAgain,
  onConfirm,
  onDontShowAgain,
}: Props) => (
  <div>
    <AnnouncementContent
      type={type}
      title={title}
      body={body}
      imageUrl={imageUrl}
      gifUrl={gifUrl ?? null}
    />

    {/* ButtonStyled rather than Button: that one reaches for the barrel. */}
    <ButtonStyled type="button" $selected $w="100%" onClick={onConfirm}>
      <span>Got It</span>
    </ButtonStyled>

    {showDontShowAgain && (
      <DontShowAgain onClick={onDontShowAgain}>
        {DONT_SHOW_AGAIN_LABEL}
      </DontShowAgain>
    )}
  </div>
);

/**
 * Everything of an announcement but its buttons: red type heading, picture,
 * bold title, markdown body. The popup's slides and this view's preview both
 * draw it, so the two cannot drift apart.
 */
export const AnnouncementContent = ({
  type,
  title,
  body,
  imageUrl,
  gifUrl,
}: {
  type: AnnouncementType;
  title: string;
  body: string;
  imageUrl: string | null;
  gifUrl: string | null;
}) => (
  <>
    <H3 $m="0 0 24px" $left $color={lightTheme.primary}>
      {ANNOUNCEMENT_TYPE_LABEL[type]}
    </H3>

    <AnnouncementMedia imageUrl={imageUrl} gifUrl={gifUrl} />

    <P $m="0 0 12px" $left $bold>
      {title}
    </P>

    <MarkdownBody>
      <ReactMarkdown>{body}</ReactMarkdown>
    </MarkdownBody>
  </>
);

/**
 * The announcement's one picture: its GIF or its image. A target carries
 * exactly one of the two, so there is nothing to fall back to.
 *
 * A click opens it full size in the chat's lightbox; a GIF keeps playing
 * there, since it is just a bigger <img>.
 */
export const AnnouncementMedia = ({
  imageUrl,
  gifUrl,
}: {
  imageUrl: string | null;
  gifUrl: string | null;
}) => {
  const [open, setOpen] = useState(false);
  const src = gifUrl ?? imageUrl;
  if (!src) return null;

  return (
    <>
      <HeroImage
        src={src}
        alt=""
        role="button"
        tabIndex={0}
        aria-label="Open image"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      />

      {/* The lightbox is portalled, but React still bubbles its events up
          through here - into the popup's swipe handlers, which would drag the
          slide under the picture. They stop at this box. */}
      <div
        onPointerDown={(e) => e.stopPropagation()}
        onPointerMove={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <ChatLightbox
          images={[{ id: src, url: src }]}
          index={open ? 0 : null}
          onIndex={() => undefined}
          onClose={() => setOpen(false)}
        />
      </div>
    </>
  );
};
